import parse from 'html-react-parser';
import React, { useEffect, useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
} from 'react-intl';
import { matchesNameCode } from '../../common';
import { isMiracleEar } from '../../hooks/miracle-ear-hook';
import { IConstants, ISession } from '../../models/pro-sites';
import { IProfile } from '../../models/profile';
import SelectInput from '../inputs/select-input';
import TextInput from '../inputs/text-input';
import UserNotificationPreferences, {
    UserNotificationPrefsErrors,
} from './user-notification-preferences';

export class ProfileFormErrors extends UserNotificationPrefsErrors {
    cellNumber? = '';
    city? = '';
    country? = '';
    firstName? = '';
    lastName? = '';
    phoneNumber? = '';
    postalCode? = '';
    secondaryEmail? = '';
    state? = '';
    street1? = '';
    onSave? = '';
}

interface IProfileFormProps {
    profile: IProfile;
    session: ISession;
    constants: IConstants;
    saving: boolean;
    errors: ProfileFormErrors;
    onSave: (event: React.ChangeEvent<HTMLFormElement>) => void;
    onChange: (event: React.ChangeEvent<HTMLElement>) => void;
}

export const ProfileForm = ({
    profile,
    session,
    constants,
    saving,
    errors,
    onSave,
    onChange,
}: IProfileFormProps) => {
    useEffect(() => {
        let value = profile.country || session.country;
        let found = constants.countries.find(matchesNameCode(value));

        if (!found && value !== session.country) {
            value = session.country;
            found = constants.countries.find(matchesNameCode(value));
        }

        if (found && found.code !== profile.country) {
            onChange({ target: { name: 'country', value: found.code } } as any);
        }
    }, [profile.country]);

    const stateValue = () => {
        const value = profile.state;
        if (!profile.state) {
            return undefined;
        }

        const found = constants.states.find(matchesNameCode(value));
        return found ? found.code : undefined;
    };

    const formInvalid = () => {
        const errorLength = Object.keys(errors).length;
        return errorLength > 0 && !(errorLength === 1 && errors.onSave);
    };

    const getPrimaryBrand = () =>
        isMiracleEar(session.siteNumber) ? 'Miracle-Ear' : session.primaryBrand;

    return (
        <form onSubmit={onSave}>
            <div className="well">
                <div className="row">
                    <div className="col-md-6">
                        <div className="form-horizontal" role="form">
                            <div className="form-group">
                                <label className="control-label col-sm-4">
                                    <FormattedMessage id="User_Name" />:
                                </label>
                                <div className="col-sm-8">
                                    <p className="form-control-static">
                                        {profile.userName}
                                    </p>
                                </div>
                            </div>
                            <TextInput
                                name="firstName"
                                label={'First Name'}
                                value={profile.firstName}
                                maxLength={30}
                                onChange={onChange}
                                required={true}
                                error={errors.firstName}
                            />
                            <TextInput
                                name="lastName"
                                label={'Last Name'}
                                value={profile.lastName}
                                onChange={onChange}
                                maxLength={30}
                                required={true}
                                error={errors.lastName}
                            />
                            <TextInput
                                name="phoneNumber"
                                label={'Phone Number'}
                                maxLength={20}
                                value={profile.phoneNumber}
                                onChange={onChange}
                                required={true}
                                error={errors.phoneNumber}
                            />
                            <TextInput
                                name="cellNumber"
                                label={'Cell Number'}
                                maxLength={20}
                                value={profile.cellNumber}
                                required={profile.smsFlag!}
                                error={errors.cellNumber}
                                onChange={onChange}
                            />
                            <TextInput
                                name="secondaryEmail"
                                label={'Secondary Email'}
                                maxLength={50}
                                value={profile.secondaryEmail}
                                onChange={onChange}
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-horizontal" role="form">
                            <TextInput
                                name="street1"
                                label={'My Address'}
                                maxLength={100}
                                value={profile.street1}
                                onChange={onChange}
                                required={true}
                                error={errors.street1}
                            />
                            <TextInput
                                name="street2"
                                label="Address Line 2"
                                maxLength={100}
                                value={profile.street2}
                                onChange={onChange}
                            />
                            <TextInput
                                name="city"
                                label={'City'}
                                maxLength={100}
                                value={profile.city}
                                onChange={onChange}
                                required={true}
                                error={errors.city}
                            />
                            {profile.country === 'US' ? (
                                <SelectInput
                                    name="state"
                                    label={'State Province'}
                                    options={constants.states.map((st) => ({
                                        value: st.code,
                                        text: st.name,
                                    }))}
                                    value={stateValue()}
                                    hasDefaultOption={true}
                                    defaultOption={'Select'}
                                    onChange={onChange}
                                    required={true}
                                    error={errors.state}
                                />
                            ) : (
                                <TextInput
                                    name="state"
                                    label={'State Province'}
                                    maxLength={50}
                                    value={profile.state}
                                    onChange={onChange}
                                    required={true}
                                    error={errors.state}
                                />
                            )}
                            <TextInput
                                name="postalCode"
                                label={'Postal Code'}
                                maxLength={50}
                                value={profile.postalCode}
                                onChange={onChange}
                            />
                            <SelectInput
                                name="country"
                                label={'Country'}
                                options={constants.countries.map((c) => ({
                                    value: c.code,
                                    text: c.name,
                                }))}
                                value={profile.country}
                                required={true}
                                hasDefaultOption={true}
                                defaultOption={'Select'}
                                error={errors.country}
                                onChange={onChange}
                            />
                        </div>
                    </div>
                    <UserNotificationPreferences
                        preferences={profile}
                        errors={errors}
                        onChange={onChange}
                    />
                    <div className="col-md-12">
                        <div className="form-group gutter-top">
                            <FormattedMessage
                                id="All_changes_made_to_this_profile"
                                values={{
                                    0: getPrimaryBrand(),
                                }}
                            />
                            {session.country === 'US' &&
                                session.contactInfo && (
                                    // tslint:disable-next-line: max-line-length
                                    <span>
                                        {' '}
                                        <FormattedMessage
                                            id={`If_you_would_like_changes`}
                                            defaultMessage={`If you would like changes to apply to your
                                    account at ${getPrimaryBrand()} please
                                    email us at`}
                                            values={{
                                                0: getPrimaryBrand(),
                                            }}
                                        />{' '}
                                        <a
                                            className="link"
                                            href={`mailto:${
                                                session.contactInfo!.email
                                            }`}
                                        >
                                            {session.contactInfo!.email}
                                        </a>
                                    </span>
                                )}
                        </div>
                    </div>
                </div>
            </div>
            {formInvalid() && (
                <div className="alert alert-danger gutter-top">
                    <span>
                        <FormattedMessage id="Please_enter_into_before_submitting" />
                    </span>
                </div>
            )}
            {errors.onSave && (
                <div className="alert alert-danger gutter-top">
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                        {parse(errors.onSave)}
                    </span>
                </div>
            )}
            <div className="gutter-top">
                <button
                    id="save-changes"
                    type="submit"
                    className="btn btn-primary"
                >
                    <span>
                        {saving ? (
                            <>
                                <FormattedMessage id="Saving" />
                                {' ...'}
                            </>
                        ) : (
                            <FormattedMessage id="Save_Changes" />
                        )}
                    </span>
                </button>
                <a
                    role="button"
                    className="btn btn-default"
                    href="/my-account/account-summary"
                >
                    <FormattedMessage id="Cancel" />
                </a>
            </div>
        </form>
    );
};

export default ProfileForm;
